import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Check, Phone, MapPin, ArrowRight, Menu, X, Sparkles, Home, Building2, Lightbulb, TreePine, Wrench, PackageCheck, PencilRuler, Zap, CalendarClock, ShieldCheck } from 'lucide-react';

const LOGO = "https://customer-assets-lqy194kg.emergentagent.net/job_90e246be-0711-4e27-8caa-13f1ea345ba5/artifacts/pl6ilas6_TNA%20Logo.png";
const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/90e246be-0711-4e27-8caa-13f1ea345ba5/images/e9f8c25f10638ffa1c3e8e01c2ce0a50357a0f1e060c03011b0d0a7d2478a9d0.jpeg";
const COMMERCIAL_IMG = "https://static.prod-images.emergentagent.com/jobs/90e246be-0711-4e27-8caa-13f1ea345ba5/images/30da8888764b7430054389325cdc647867f5f02902a0c516208ae9dd136a8cc4.jpeg";
const CONTACT_URL = '/?service=' + encodeURIComponent('Christmas & Holiday Lighting') + '#contact';

const PAGE_TITLE = 'Christmas Light Installation Calgary | Commercial & Residential Holiday Lighting | True North Alberta';
const PAGE_DESC = 'Professional Christmas light installation in Calgary & Alberta. Custom holiday lighting for homes, estates, storefronts, HOAs & condos using commercial-grade C9 LED bulbs and 5mm wide-angle LEDs. Design, install, maintenance & takedown. Contact us for details.';

const SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "Christmas & Holiday Lighting Installation",
  "serviceType": "Christmas Light Installation",
  "url": "https://truenorthalberta.ca/services/holiday-lighting",
  "description": PAGE_DESC,
  "provider": {
    "@type": "LocalBusiness",
    "name": "True North Alberta Property Services",
    "telephone": "587-325-2799",
    "url": "https://truenorthalberta.ca",
    "address": { "@type": "PostalAddress", "addressLocality": "Calgary", "addressRegion": "Alberta", "addressCountry": "CA" }
  },
  "areaServed": [{ "@type": "City", "name": "Calgary" }, { "@type": "State", "name": "Alberta" }],
  "audience": [{ "@type": "Audience", "audienceType": "Residential" }, { "@type": "Audience", "audienceType": "Commercial" }],
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "Holiday Lighting Services",
    "itemListElement": [
      "Roofline C9 LED Lighting", "5mm Wide-Angle LED Tree Wrapping", "Commercial Storefront Holiday Lighting",
      "HOA & Condominium Holiday Displays", "Wreaths & Garland Installation", "Holiday Lighting Maintenance & Takedown"
    ].map(name => ({ "@type": "Offer", "itemOffered": { "@type": "Service", "name": name } }))
  }
};

const products = [
  { icon: Lightbulb, title: "C9 LED Bulbs", tag: "Roofline Signature", desc: "The classic, large-profile bulb seen on premium homes and storefronts. Commercial-grade C9 LEDs deliver a bold, evenly spaced glow along rooflines, ridges, peaks and parapets - custom cut and clipped to fit your architecture exactly." },
  { icon: Sparkles, title: "5mm Wide-Angle LEDs", tag: "Trees & Greenery", desc: "Also known as conical or coax mini lights. The wide-angle lens throws light in every direction for brilliant, full-coverage tree wrapping, canopy lighting, shrubs, garland and wreaths - with no dark spots." },
  { icon: TreePine, title: "Tree Wrapping & Canopy", tag: "Statement Pieces", desc: "Trunk wraps, branch wraps and full canopy lighting on mature spruce, pine and deciduous trees. Transform your yard or entry landscape into a true winter showpiece." },
  { icon: Zap, title: "Wreaths, Garland & Accents", tag: "Finishing Touches", desc: "Pre-lit commercial wreaths, lit garland over entries and railings, pathway stakes, window frames and column wraps to complete a cohesive, professionally designed display." }
];

const process = [
  { icon: PencilRuler, step: "01", title: "Design Consultation", desc: "We walk your property, discuss colour palette and style, and design a display that complements the architecture and landscape." },
  { icon: Wrench, step: "02", title: "Professional Installation", desc: "Insured, trained installers use commercial clips, custom-cut lines and weather-rated connections. No staples, no nails, no damage." },
  { icon: ShieldCheck, step: "03", title: "Season-Long Maintenance", desc: "If a bulb goes out or a line shifts in a chinook wind, we come back and fix it. Your display looks perfect all season." },
  { icon: PackageCheck, step: "04", title: "Takedown & Storage", desc: "After the holidays we carefully remove everything, label and store your display ready for next year." }
];

const audiences = [
  { icon: Home, title: "Residential & Estates", items: ["Single-family homes and acreages", "Private estates and luxury residences", "Roofline, trees, entries and pathways", "Warm white, pure white, multicolour or custom"] },
  { icon: Building2, title: "Commercial Properties", items: ["Storefronts, retail plazas and restaurants", "Office buildings and corporate campuses", "HOAs and condominium complexes", "Multi-year commercial lighting programs"] }
];

const HolidayLighting = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const prevTitle = document.title;
    document.title = PAGE_TITLE;
    const setMeta = (selector, attr, value) => {
      let el = document.head.querySelector(selector);
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr.split('=')[0], attr.split('=')[1]); document.head.appendChild(el); }
      const prev = el.getAttribute('content');
      el.setAttribute('content', value);
      return () => { if (prev) el.setAttribute('content', prev); };
    };
    const restores = [
      setMeta('meta[name="description"]', 'name=description', PAGE_DESC),
      setMeta('meta[property="og:title"]', 'property=og:title', PAGE_TITLE),
      setMeta('meta[property="og:description"]', 'property=og:description', PAGE_DESC),
      setMeta('meta[property="og:image"]', 'property=og:image', HERO_IMG),
    ];
    const canonical = document.head.querySelector('link[rel="canonical"]');
    const prevCanonical = canonical?.getAttribute('href');
    canonical?.setAttribute('href', 'https://truenorthalberta.ca/services/holiday-lighting');
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(SCHEMA);
    document.head.appendChild(script);
    window.scrollTo(0, 0);
    return () => {
      document.title = prevTitle;
      restores.forEach(r => r());
      if (prevCanonical) canonical.setAttribute('href', prevCanonical);
      script.remove();
    };
  }, []);

  const goToContact = () => { window.location.href = CONTACT_URL; };

  return (
    <div className="min-h-screen bg-slate-950" data-testid="holiday-lighting-page">
      {/* Premium Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-slate-950/98 via-emerald-950/95 to-slate-950/98 backdrop-blur-xl border-b border-emerald-500/20 shadow-[0_4px_30px_rgba(16,185,129,0.15)]">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between h-20 md:h-28">
            <Link to="/" className="flex items-center group -ml-2 md:-ml-4 mt-1 md:mt-2">
              <div className="relative">
                <div className="absolute -inset-2 md:-inset-3 bg-gradient-to-r from-emerald-500/40 via-green-400/30 to-emerald-500/40 rounded-2xl blur-xl opacity-70 group-hover:opacity-100 transition-opacity duration-500"></div>
                <img src={LOGO} alt="True North Alberta" className="relative h-16 md:h-24 w-auto rounded-xl shadow-[0_8px_32px_rgba(0,0,0,0.4),0_0_60px_rgba(16,185,129,0.3)] border-2 border-emerald-400/30" style={{filter: 'drop-shadow(0 0 20px rgba(16,185,129,0.4))'}} />
              </div>
            </Link>
            <nav className="hidden md:flex items-center gap-6">
              {[['/services','Services'],['/commercial','Commercial'],['/gallery','Gallery'],['/values','Our Values'],['/testimonials','Testimonials'],['/faq','FAQ']].map(([to, label]) => (
                <Link key={to} to={to} className="relative text-gray-200 hover:text-emerald-400 transition-all duration-300 font-semibold text-sm uppercase tracking-wider group">
                  {label}
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-emerald-400 to-green-400 group-hover:w-full transition-all duration-300"></span>
                </Link>
              ))}
              <Button onClick={goToContact} data-testid="header-quote-btn" className="relative overflow-hidden bg-gradient-to-r from-emerald-600 via-green-500 to-emerald-600 hover:from-emerald-500 hover:via-green-400 hover:to-emerald-500 text-white font-bold px-8 py-3 rounded-full shadow-[0_4px_20px_rgba(16,185,129,0.4)] transition-all duration-300 border border-emerald-400/30">
                Get a Quote
              </Button>
            </nav>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-toggle" className="md:hidden p-2 text-white hover:text-emerald-400 transition-colors" aria-label="Toggle menu">
              {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-slate-950 border-b border-emerald-500/20 shadow-2xl z-50">
            <nav className="container mx-auto px-6 py-6 flex flex-col gap-1">
              {[['/services','Services'],['/commercial','Commercial'],['/gallery','Gallery'],['/values','Our Values'],['/testimonials','Testimonials'],['/faq','FAQ']].map(([to, label]) => (
                <Link key={to} to={to} onClick={() => setMobileMenuOpen(false)} className="text-gray-200 hover:text-emerald-400 hover:bg-emerald-500/10 transition-all duration-300 font-semibold text-lg py-3 px-4 rounded-lg">{label}</Link>
              ))}
              <Button onClick={() => { goToContact(); setMobileMenuOpen(false); }} className="mt-4 bg-gradient-to-r from-emerald-600 via-green-500 to-emerald-600 text-white font-bold px-8 py-3 rounded-full shadow-lg">Get a Quote</Button>
            </nav>
          </div>
        )}
      </header>

      {/* Hero */}
      <section className="relative min-h-[92vh] flex items-center pt-28 pb-24 overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Luxury Alberta home with professionally installed warm white C9 Christmas lights on the roofline and lit spruce trees" className="w-full h-full object-cover animate-ken-burns" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/95 via-slate-950/75 to-slate-950/30"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-slate-950/60"></div>
        <div className="absolute top-1/3 left-10 w-96 h-96 bg-amber-400/10 rounded-full blur-3xl"></div>

        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-400/10 border border-amber-400/30 rounded-full mb-8 backdrop-blur-sm">
              <Sparkles className="w-4 h-4 text-amber-300" />
              <span className="text-amber-300 text-sm font-semibold uppercase tracking-widest">Residential & Commercial</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-6 leading-[1.05]" data-testid="holiday-hero-title">
              Christmas & Holiday Lighting <span className="block bg-gradient-to-r from-amber-200 via-yellow-100 to-amber-300 bg-clip-text text-transparent">Installed to Perfection</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-200 mb-4 leading-relaxed max-w-2xl">
              Professional Christmas light installation in Calgary and across Alberta. We design, install, maintain and remove stunning holiday displays for homes, private estates, storefronts, HOAs and condominiums.
            </p>
            <p className="text-base text-gray-400 mb-10 max-w-2xl">
              Commercial-grade C9 LED bulbs. 5mm wide-angle LED tree wrapping. Zero ladders for you. This is more than lights - it's an experience your family, tenants and customers look forward to every year.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button onClick={goToContact} data-testid="hero-contact-btn" className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-950 font-bold px-8 py-4 rounded-full text-lg shadow-[0_8px_30px_rgba(245,158,11,0.35)] transition-all duration-300 hover:-translate-y-0.5">
                Contact Us for Details
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <a href="tel:5873252799">
                <Button variant="outline" className="border-2 border-white/30 text-white hover:bg-white/10 font-bold px-8 py-4 rounded-full text-lg backdrop-blur-sm">
                  <Phone className="mr-2 w-5 h-5" /> 587-325-2799
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Experience strip */}
      <section className="py-14 px-4 lg:px-8 bg-slate-900 border-y border-amber-500/10">
        <div className="container mx-auto">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {["Custom design for your architecture", "Commercial-grade LED products", "Installed by insured professionals", "Maintenance, takedown & storage included"].map((text, idx) => (
              <div key={idx} className="flex items-center gap-3 bg-slate-800/50 p-4 rounded-xl border border-slate-700/50">
                <div className="w-8 h-8 bg-gradient-to-br from-amber-400 to-amber-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <Check className="w-5 h-5 text-slate-950" />
                </div>
                <span className="text-gray-200 font-medium">{text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Products */}
      <section className="py-24 px-4 lg:px-8 bg-slate-950">
        <div className="container mx-auto">
          <div className="max-w-3xl mb-16">
            <span className="text-amber-400 text-sm font-semibold uppercase tracking-widest">Commercial-Grade Products</span>
            <h2 className="text-3xl md:text-5xl font-black text-white mt-3 mb-4">
              The Right Light for <span className="bg-gradient-to-r from-amber-200 to-amber-400 bg-clip-text text-transparent">Every Feature</span>
            </h2>
            <p className="text-lg text-gray-400">
              Big-box string lights fail in Alberta winters. We install professional products designed for -40°C, chinook winds and years of use - then design with them like an artist.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {products.map((p, idx) => (
              <Card key={idx} data-testid={`product-card-${idx}`} className="group bg-slate-900/60 backdrop-blur-sm border-2 border-slate-700/50 hover:border-amber-400/50 rounded-3xl transition-all duration-500 hover:shadow-[0_20px_60px_rgba(245,158,11,0.15)] hover:-translate-y-1">
                <CardHeader>
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-amber-400 to-amber-600 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-500">
                      <p.icon className="w-7 h-7 text-slate-950" />
                    </div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-amber-300/80 bg-amber-400/10 px-3 py-1 rounded-full">{p.tag}</span>
                  </div>
                  <CardTitle className="text-2xl text-white group-hover:text-amber-200 transition-colors">{p.title}</CardTitle>
                  <CardDescription className="text-gray-400 text-base leading-relaxed">{p.desc}</CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Commercial + Residential */}
      <section className="py-24 px-4 lg:px-8 bg-gradient-to-b from-slate-900 to-slate-950">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            <div className="relative order-2 lg:order-1">
              <div className="absolute -inset-4 bg-gradient-to-r from-amber-500/20 to-emerald-500/20 rounded-3xl blur-xl"></div>
              <img src={COMMERCIAL_IMG} alt="Commercial office building and retail plaza in Calgary with professional holiday lighting on the roofline and wrapped trees" className="relative rounded-3xl shadow-2xl w-full object-cover aspect-[3/2]" />
            </div>
            <div className="order-1 lg:order-2">
              <span className="text-amber-400 text-sm font-semibold uppercase tracking-widest">Who We Serve</span>
              <h2 className="text-3xl md:text-5xl font-black text-white mt-3 mb-6">
                Homes, Estates & <span className="bg-gradient-to-r from-amber-200 to-amber-400 bg-clip-text text-transparent">Commercial Properties</span>
              </h2>
              <p className="text-lg text-gray-400 mb-8">
                From a single family home to a multi-building HOA or a retail plaza that needs to draw shoppers in the dark December evenings, we scale the same premium experience to fit your property.
              </p>
              <div className="grid sm:grid-cols-2 gap-6">
                {audiences.map((a, idx) => (
                  <div key={idx} className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6">
                    <a.icon className="w-8 h-8 text-amber-400 mb-4" />
                    <h3 className="text-lg font-bold text-white mb-3">{a.title}</h3>
                    <ul className="space-y-2">
                      {a.items.map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-gray-300 text-sm">
                          <Check className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />{item}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-24 px-4 lg:px-8 bg-slate-950">
        <div className="container mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-amber-400 text-sm font-semibold uppercase tracking-widest">The Full-Service Experience</span>
            <h2 className="text-3xl md:text-5xl font-black text-white mt-3 mb-4">
              You Enjoy the Season. <span className="bg-gradient-to-r from-amber-200 to-amber-400 bg-clip-text text-transparent">We Handle the Rest.</span>
            </h2>
            <p className="text-lg text-gray-400">No ladders, no tangled boxes, no burnt-out strands. Just a beautiful display from the first snow to the New Year.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {process.map((s, idx) => (
              <div key={idx} data-testid={`process-step-${idx}`} className="relative bg-slate-900/60 border border-slate-700/50 rounded-3xl p-8 hover:border-amber-400/40 transition-colors duration-300">
                <span className="absolute top-6 right-6 text-5xl font-black text-white/5">{s.step}</span>
                <div className="w-12 h-12 bg-amber-400/10 border border-amber-400/30 rounded-xl flex items-center justify-center mb-6">
                  <s.icon className="w-6 h-6 text-amber-300" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{s.title}</h3>
                <p className="text-gray-400 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Booking notice + CTA */}
      <section className="py-24 px-4 lg:px-8 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-amber-950/60 via-slate-900 to-emerald-950/60"></div>
        <div className="absolute inset-0 opacity-20">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="container mx-auto relative z-10 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-400/10 border border-amber-400/30 rounded-full mb-8">
            <CalendarClock className="w-4 h-4 text-amber-300" />
            <span className="text-amber-300 text-sm font-semibold uppercase tracking-widest">Installations run October to December - schedules fill fast</span>
          </div>
          <h2 className="text-3xl md:text-5xl font-black text-white mb-6">
            Let's Light Up Your Property This Season
          </h2>
          <p className="text-lg md:text-xl text-gray-300 mb-10 max-w-2xl mx-auto">
            Every display is custom. Tell us about your home or commercial property and we'll design a holiday lighting experience built around it. Contact us for details and availability.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button onClick={goToContact} data-testid="cta-contact-btn" className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-950 font-bold px-10 py-4 rounded-full text-lg shadow-[0_8px_30px_rgba(245,158,11,0.35)]">
              Contact Us for Details
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <a href="tel:5873252799">
              <Button variant="outline" className="border-2 border-white/30 text-white hover:bg-white/10 font-bold px-8 py-4 rounded-full text-lg">
                <Phone className="mr-2 w-5 h-5" /> Call 587-325-2799
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 border-t border-slate-800 py-16 px-4 lg:px-8">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-4 gap-12">
            <div className="md:col-span-2">
              <Link to="/" className="inline-block mb-6">
                <img src={LOGO} alt="True North Alberta" className="h-20 w-auto rounded-xl" />
              </Link>
              <p className="text-gray-400 mb-6 max-w-md">
                Alberta's trusted property services company. Professional landscaping, snow removal and holiday lighting for residential and commercial properties.
              </p>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><Link to="/services" className="text-gray-400 hover:text-emerald-400 transition-colors">Services</Link></li>
                <li><Link to="/commercial" className="text-gray-400 hover:text-emerald-400 transition-colors">Commercial</Link></li>
                <li><Link to="/gallery" className="text-gray-400 hover:text-emerald-400 transition-colors">Gallery</Link></li>
                <li><Link to="/faq" className="text-gray-400 hover:text-emerald-400 transition-colors">FAQ</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Contact</h4>
              <ul className="space-y-3">
                <li className="flex items-center gap-2 text-gray-400">
                  <Phone className="w-4 h-4 text-emerald-400" />
                  <a href="tel:5873252799" className="hover:text-emerald-400 transition-colors">587-325-2799</a>
                </li>
                <li className="flex items-center gap-2 text-gray-400">
                  <MapPin className="w-4 h-4 text-emerald-400" />
                  Calgary & Surrounding Areas
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 mt-12 pt-8 text-center text-gray-500">
            <p>&copy; {new Date().getFullYear()} True North Alberta Property Services. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HolidayLighting;
